import React from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Modal from '@amzn/awsui-components-core/modal';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useDatasetWizard } from 'src/components/star-grid/datasets/hooks/useDatasetWizard';
import type { Dataset } from 'src/components/star-grid/datasets/datasetTypes';
import { WIZARD_STEP_TITLE_KEYS, isLastStep, stepIndex } from 'src/components/star-grid/datasets/utils/wizardSteps';
import { WizardSourceStep } from 'src/components/star-grid/datasets/components/WizardSourceStep';
import { WizardAccessStep } from 'src/components/star-grid/datasets/components/WizardAccessStep';
import { WizardMapColumnsStep } from 'src/components/star-grid/datasets/components/WizardMapColumnsStep';
import { WizardScenariosStep } from 'src/components/star-grid/datasets/components/WizardScenariosStep';
import { WizardReviewStep } from 'src/components/star-grid/datasets/components/WizardReviewStep';
import styles from 'src/components/star-grid/datasets/components/styles/DatasetWizard.module.scss';

interface Props {
  readonly text: StarGridText;
  readonly visible: boolean;
  readonly onDismiss: () => void;
  readonly onSaved: (dataset: Dataset) => void;
  /** When set, the wizard edits this dataset — it opens at Map Columns with the source, filters and access fixed. */
  readonly editingId?: string;
}

export const DatasetWizard: React.FC<Props> = ({ text, visible, onDismiss, onSaved, editingId }) => {
  const state = useDatasetWizard({ onSaved, editingId });
  const current = stepIndex(state.step, state.steps);
  const last = isLastStep(state.step, state.steps);

  const body =
    state.step === 'source' ? (
      <WizardSourceStep state={state} text={text} />
    ) : state.step === 'access' ? (
      <WizardAccessStep state={state} text={text} />
    ) : state.step === 'map' ? (
      <WizardMapColumnsStep state={state} text={text} />
    ) : state.step === 'scenarios' ? (
      <WizardScenariosStep state={state} text={text} />
    ) : (
      <WizardReviewStep state={state} text={text} />
    );

  const footer = (
    <SpaceBetween direction="horizontal" size="xs">
      <Button onClick={onDismiss} data-testid="wizard-cancel">
        {text('wizardCancel')}
      </Button>
      <Button disabled={current === 0} onClick={state.back} data-testid="wizard-back">
        {text('wizardBack')}
      </Button>
      {last ? (
        state.saved ? (
          <Button variant="primary" onClick={onDismiss}>
            {text('wizardCancel')}
          </Button>
        ) : (
          <Button variant="primary" disabled={!state.canAdvance} loading={state.saving} onClick={state.save} data-testid="wizard-save">
            {text('wizardSave')}
          </Button>
        )
      ) : (
        <Button variant="primary" disabled={!state.canAdvance} onClick={state.next} data-testid="wizard-next">
          {text('wizardNext')}
        </Button>
      )}
    </SpaceBetween>
  );

  return (
    <Modal
      visible={visible}
      size="large"
      header={text(state.editing ? 'wizardEditTitle' : 'wizardTitle')}
      onDismiss={onDismiss}
      footer={footer}
      data-testid="dataset-wizard"
    >
      {state.editing && state.readOnlyRef && (
        <Box margin={{ bottom: 's' }} color="text-body-secondary" data-testid="wizard-readonly-ref">
          {text('wizardSourceReadOnly')}: {state.readOnlyRef}
        </Box>
      )}
      <ol className={styles.steps} aria-label={text(state.editing ? 'wizardEditTitle' : 'wizardTitle')}>
        {state.steps.map((step, index) => (
          <li key={step} className={`${styles.step} ${index === current ? styles.stepActive : ''}`}>
            <span className={styles.stepIndex}>{index + 1}</span>
            {text(WIZARD_STEP_TITLE_KEYS[step])}
          </li>
        ))}
      </ol>
      {state.error && state.step !== 'review' && (
        <Alert type="error" data-testid="wizard-error">
          {state.error}
        </Alert>
      )}
      {body}
    </Modal>
  );
};
